import { Breadcrumbs, PageTitle } from '@sleepstation/components'
import { CopyParagraph } from '@sleepstation/components-copy'
import { VLayout } from '@sleepstation/components-layouts'
import { ASPECT_RATIOS, LAYOUT_SPACING } from '@sleepstation/const'
import { paddingBottom } from '@sleepstation/style-api-helpers'
import PropTypes from 'prop-types'
import { toLower } from 'ramda'
import React from 'react'
import styled from 'styled-components'
import { RESOURCE_TYPES } from '../../../../const/resources'
import BreakoutModuleNarrow from '../../../shared/breakoutModules/BreakoutModuleNarrow'
import Transcription from './Transcription'
import AudioContent from './content/AudioContent'
import PDFContent from './content/PDFContent'
import VideoContent from './content/VideoContent'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const PAGES = [
  {
    title: `Resources`,
    path: `/resources/`,
  },
]

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const renderResourceForType = ({
  type,
  videoId,
  aspectRatio,
  videoProvider,
  contentUrl,
}) => {
  const resolvedAspectRatio = aspectRatio || ASPECT_RATIOS.SIXTEEN_NINE

  if (toLower(type) === RESOURCE_TYPES.VIDEO) {
    return (
      <VideoContent
        videoId={videoId}
        aspectRatio={resolvedAspectRatio}
        provider={videoProvider}
      />
    )
  }

  if (toLower(type) === RESOURCE_TYPES.AUDIO) {
    return <AudioContent url={contentUrl} />
  }

  if (toLower(type) === RESOURCE_TYPES.PDF) {
    return <PDFContent url={contentUrl} />
  }

  throw new Error(`Can’t render resource of type: '${type}'`)
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(VLayout).attrs({ spacing: LAYOUT_SPACING.LARGE })``

const Header = styled(VLayout).attrs({ spacing: [0, LAYOUT_SPACING.SMALL] })``

const Body = styled(VLayout).attrs({ spacing: [LAYOUT_SPACING.LARGE] })``

const Primary = styled(VLayout).attrs({
  spacing: [1, LAYOUT_SPACING.LARGE],
})``

const Nav = styled.nav`
  ${paddingBottom([0, 0])}
`

const Description = styled(CopyParagraph)`
  white-space: pre-wrap;
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const Resource = ({ resource, content }) => {
  const {
    title,
    description,
    type,
    contentUrl,
    videoId,
    aspectRatio,
    videoProvider,
    transcription,
  } = resource

  const hasTranscription = toLower(type) === RESOURCE_TYPES.VIDEO

  const shouldRenderTranscription = !!(hasTranscription && transcription)

  const hasBorder = shouldRenderTranscription

  return (
    <Layout>
      <BreakoutModuleNarrow
        hasGradient={hasBorder}
        hasBottomBorder={hasBorder}
        hideBorderAtXSmall
        paddingV={[
          [1, 0.5],
          [1.5, 1.5],
          [1.5, 1.5],
          [2, 1.75],
        ]}
      >
        <Primary>
          <Nav>
            <Breadcrumbs pages={PAGES} />
          </Nav>
          <Header>
            <PageTitle data-cy="title">{title}</PageTitle>
          </Header>
          <Body>
            {description && <Description>{description}</Description>}
            {renderResourceForType({
              type,
              contentUrl,
              videoId,
              aspectRatio,
              videoProvider,
            })}
          </Body>
        </Primary>
      </BreakoutModuleNarrow>
      {shouldRenderTranscription && (
        <BreakoutModuleNarrow
          hasGradient={false}
          hasBottomBorder={false}
          paddingV={[
            [0, 3],
            [1, 4],
            [1, 4],
            [0.5, 4],
          ]}
        >
          <Transcription
            title={content.transcription.title}
            transcription={transcription}
          />
        </BreakoutModuleNarrow>
      )}
    </Layout>
  )
}

Resource.propTypes = {
  resource: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
}

Resource.defaultProps = {}

export default Resource
